import tables, {PAGE_SIZES} from 'constants/tables'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import {Colors} from 'appearance'
import {styled} from 'common'
import meterReadingStatus from 'constants/enum/meterReadingStatus'
import colors from 'appearance/colors'
import {useGetCurrentUserQuery, useIsRtl, useUpdateStatusMutation} from 'store/Store'
import Tooltip from 'components/Display/Tooltip/Tooltip'
import isNil from 'lodash/isNil'
import Switch from 'components/Control/Switch'
import {useTranslation} from 'react-i18next'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import {Box, Chip, Typography} from '@mui/material'
import dayjs from 'dayjs'
import {beforeCloseModalOrResetAlert} from 'services/alert'
import {toast} from 'sonner'
import WestRoundedIcon from '@mui/icons-material/WestRounded'
export const setSortDirection = (itemName, itemKey, dir, setUrlParam) => {
    const parserSort = {[itemKey]: dir}
    setUrlParam(`${itemName}_sort`, JSON.stringify(parserSort))
}
export const getSortDirection = (itemKey, sort) => {
    const parserSort = sort ? JSON.parse(sort) : {}
    return parserSort[itemKey]
}
const date = (value) => (value && dayjs(value).format('D/M/YYYY')) ?? 'N/A'
const datetime = (value) =>
    value ? (
        <Box display="flex" lineHeight={1.25} gap={0.5} justifyContent={'center'}>
            <Typography variant="caption" sx={{fontWeight: 600}}>
                {dayjs(value).format('D/M/YYYY')}
            </Typography>
            <Typography variant="caption" sx={{color: Colors.gray}}>
                {dayjs(value).format('HH:mm')}
            </Typography>
        </Box>
    ) : (
        'N/A'
    )
const number = (value) => value ?? 'N/A'
const numberFixed = (value) => (value ? value.toFixed(2) : 'N/A')
const boolean = (value) =>
    value ? (
        <CheckRoundedIcon style={{color: Colors.success}} />
    ) : (
        <CloseRoundedIcon style={{color: Colors.danger}} />
    )
// Infers a start/end on circular ranges by locating the largest gap in the sequence.
const computeCircularRangeBoundaries = (value, period) => {
    if (!Array.isArray(value) || value.length === 0) return null

    const values = Array.from(
        new Set(value.map((val) => Number(val)).filter((val) => Number.isFinite(val)))
    ).sort((a, b) => a - b)

    if (!values.length) return null

    if (values.length === 1) {
        return {start: values[0], end: values[0]}
    }

    let maxDiff = -1
    let maxDiffIndex = values.length - 1
    for (let idx = 0; idx < values.length; idx++) {
        const current = values[idx]
        const next = values[(idx + 1) % values.length]
        let diff = next - current

        if (period !== undefined) {
            diff = (next - current + period) % period || period
        }

        if (diff > maxDiff) {
            maxDiff = diff
            maxDiffIndex = idx
        }
    }

    const hasGap = period !== undefined ? maxDiff > 1 : maxDiff > 0

    const start = hasGap ? values[(maxDiffIndex + 1) % values.length] : values[0]
    const end = hasGap ? values[maxDiffIndex] : values[values.length - 1]

    return {start, end}
}

const pad2 = (val) => String(val).padStart(2, '0')

const RangeChip = ({label, value}) => (
    <Box
        sx={{
            px: 0.75,
            py: 0.3,
            borderRadius: 1,
            backgroundColor: Colors.LIGHT_GRAY,
            minWidth: 64,
            textAlign: 'center',
        }}>
        <Typography
            variant="overline"
            sx={{display: 'block', color: Colors.gray, lineHeight: 1.1, fontSize: 9}}>
            {label}
        </Typography>
        <Typography variant="caption" sx={{fontWeight: 600, fontSize: 11}}>
            {value}
        </Typography>
    </Box>
)

const RangeDisplay = ({boundaries, type}) => {
    const {t} = useTranslation()
    const isRtl = useIsRtl()
    const notAvailable = 'N/A'

    if (!boundaries) return notAvailable
    const {start, end} = boundaries
    if (start === undefined || end === undefined) return notAvailable

    const isSingle = start === end
    const startLabel = t('tableRanges.start')
    const endLabel = t('tableRanges.end')
    const singleLabel = type === 'hour' ? t('tableRanges.hour') : t('tableRanges.month')

    const formatValue = (value, position) => {
        if (type === 'hour') {
            return `${pad2(value)}:${position === 'end' ? '59' : '00'}`
        }
        return String(value)
    }

    if (isSingle) {
        const singleValue = type === 'hour' ? `${pad2(start)}:00` : String(start)
        return (
            <Box display="flex" alignItems="center" justifyContent="center">
                <RangeChip label={singleLabel} value={singleValue} />
            </Box>
        )
    }

    const startChip = <RangeChip label={startLabel} value={formatValue(start, 'start')} />
    const endChip = <RangeChip label={endLabel} value={formatValue(end, 'end')} />

    return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.75}>
            {startChip}
            <WestRoundedIcon
                sx={{
                    color: Colors.gray,
                    transform: !isRtl ? 'rotate(180deg)' : 'none',
                    fontSize: 'small',
                }}
            />
            {endChip}
        </Box>
    )
}

const range = (value) => (
    <RangeDisplay boundaries={computeCircularRangeBoundaries(value, 12)} type="month" />
)

const rangeHours = (value) => {
    const boundaries = computeCircularRangeBoundaries(value, 24)
    return <RangeDisplay boundaries={boundaries} type="hour" />
}

const tokenTimeLeft = (value) => {
    if (!value) return 'N/A'
    const now = dayjs()
    const expiry = dayjs(value)
    const daysLeft = expiry.diff(now, 'day')

    if (daysLeft < 0) {
        return <Chip size="small" label="Expired" color="error" />
    }
    const color = daysLeft <= 7 ? 'warning' : 'success'
    return <Chip size="small" label={`${daysLeft}d`} color={color} />
}

const price = (value) => (value ? `${value} ₪` : 'N/A')
const priceFixed = (value) => (value ? `${value.toFixed(2)} ₪` : 'N/A')

const invoiceNumber = (value) => (isNil(value) ? value : String(value).padStart(9, '0'))

const RateName = (value, externalValues) => {
    const {isLive} = externalValues
    const {t} = useTranslation()
    return (
        <Box display="flex" alignItems="center" gap={0.5} justifyContent={'center'}>
            {isLive && (
                <FiberManualRecordRoundedIcon style={{color: colors.success, fontSize: 12}} />
            )}
            {t(`selectOptions.${value}`)}
        </Box>
    )
}
const StatusMeterReading = (value, externalValues) => {
    const {invoice_number, failure_details, is_new_meter} = externalValues
    const {t} = useTranslation()
    const invoiceNumberTittle = `${t('invoices.invoice_number')}: ${invoiceNumber(invoice_number)}`
    const failureDetailsTittle =
        failure_details?.length > 0
            ? `${t('meter_readings.failure_details')}: ${failure_details?.map((detail) =>
                  t(`failure_details.${detail}`)
              )}`
            : ''

    if (value === meterReadingStatus.PENDING && invoice_number) value = meterReadingStatus.ASSIGNED

    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
            <Tooltip title={invoice_number ? invoiceNumberTittle : failureDetailsTittle} arrow>
                <StatusMeterReadingContainer
                    style={{cursor: 'pointer'}}
                    color={colors[meterReadingStatus.STATUS_READING_COLOR[value]]}>
                    {t(`selectOptions.${value}`)}
                </StatusMeterReadingContainer>
            </Tooltip>
            {is_new_meter && (
                <Tooltip title={t('meter_readings.is_new_meter')} arrow>
                    <Chip
                        size="small"
                        label={t('meter_readings.newMeterShort')}
                        sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 700,
                            backgroundColor: '#e0f2fe',
                            color: '#0369a1',
                            cursor: 'default',
                            '& .MuiChip-label': {px: 0.75},
                        }}
                    />
                </Tooltip>
            )}
        </Box>
    )
}

const UpdateIsPaid = (value, externalValues) => {
    const {_id, status} = externalValues
    const {t} = useTranslation()
    const [doUpdate] = useUpdateStatusMutation()
    const {data: userToken} = useGetCurrentUserQuery()
    const {isSuperUser} = userToken || {}
    const nextStateKey = value ? 'unpaid' : 'paid'
    const isCancelled = status === 'CANCELLED'

    const onChange = () =>
        beforeCloseModalOrResetAlert({
            title: t(`invoices.is_paid_status.close_modal_title.${nextStateKey}`),
            onConfirm: () =>
                toast.promise(
                    doUpdate({
                        id: _id,
                        data: {is_paid: !value},
                    }).unwrap(),
                    {
                        loading: t('loading'),
                        success: () => t(`invoices.is_paid_status.success.${nextStateKey}`),
                        error: (err) => err?.data?.error || t('invoices.submit.edit.error'),
                    }
                ),
            t,
            confirmText: t(`invoices.is_paid_status.confirm_text.${nextStateKey}`),
        })
    const tooltipTitle = isCancelled
        ? t('invoices.cancelled_status')
        : t(`invoices.is_paid_status.${value.toString()}`)

    if (isCancelled) {
        return (
            <Tooltip title={tooltipTitle} arrow>
                <span style={{cursor: 'not-allowed'}}>
                    <Switch checked={value} disabled color="error" />
                </span>
            </Tooltip>
        )
    }

    return (
        <Tooltip title={tooltipTitle} arrow>
            <span style={{cursor: 'pointer'}}>
                <Switch
                    checked={value}
                    disabled={isSuperUser ? false : value}
                    color="primary"
                    onChange={onChange}
                />
            </span>
        </Tooltip>
    )
}

const ValueWithOriginal = (value, externalValues) => {
    const {original_value} = externalValues || {}
    const display = value != null ? value.toFixed(2) : 'N/A'
    if (original_value == null) return display
    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.2}>
            <span>{display}</span>
            <Typography
                variant="caption"
                sx={{color: Colors.gray, fontSize: 10, lineHeight: 1.2}}>
                {`(${original_value.toFixed(2)})`}
            </Typography>
        </Box>
    )
}

const tooltip = (value) =>
    value != null ? (
        <Tooltip title={String(value)} placement="top" arrow>
            <Typography variant="inherit" noWrap>
                {value}
            </Typography>
        </Tooltip>
    ) : (
        'N/A'
    )

export const getValueTableByType = {
    date,
    datetime,
    number,
    boolean,
    rangeHours,
    range,
    price,
    numberFixed,
    priceFixed,
    StatusMeterReading,
    invoiceNumber,
    UpdateIsPaid,
    RateName,
    tokenTimeLeft,
    tooltip,
    ValueWithOriginal,
}

const getParsedSort = (sort) => {
    if (sort) {
        return JSON.parse(sort)
    }
    return {}
}
export const getItemId = (item) => item._id || item.id || item.key

const sortByItemKey = (itemKey, direction) => (item1, item2) => {
    const equality =
        direction === 'asc' ? item1[itemKey] < item2[itemKey] : item1[itemKey] > item2[itemKey]

    return equality ? -1 : 1
}

const getSortedData = (sort, searchedData) => {
    const parsedSort = getParsedSort(sort)
    const sortKey = Object.keys(parsedSort)[0]

    let sortedData = [...searchedData]
    if (sortKey) {
        sortedData.sort(sortByItemKey(sortKey, parsedSort[sortKey]))
    }

    return sortedData
}

const getVisibleData = (visibleKeys, data) => {
    if (visibleKeys) {
        return data?.map((row) => {
            return visibleKeys.reduce((accu, key) => {
                if (key === 'role' && row?.account?.role) {
                    accu[key] = row.account.role
                } else {
                    accu[key] = row[key]
                }
                return accu
            }, {})
        })
    }

    return data
}
export const isSearchedRow = (row, search) =>
    !search ||
    Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(search.toLowerCase())
    )

const extractTableData = (data, visibleKeys, page, pageSize, search, sort, paginationType) => {
    page = Number(page || 0)
    pageSize = Number(pageSize || PAGE_SIZES[0])
    if (paginationType === 'local') {
        data = data.slice(page * pageSize, (page + 1) * pageSize)
    }

    const visibleData = getVisibleData(visibleKeys, data)

    if (paginationType === 'external') {
        return visibleData
    }

    const searchedData = visibleData?.filter((row) => isSearchedRow(row, search))

    return getSortedData(sort, searchedData)
}
const extractHeaderKeys = (data) => (data.length > 0 ? Object.keys(data[0]) : [])

export const getTableData = (
    name,
    visibleKeys,
    data,
    page,
    pageSize,
    search,
    sort,
    paginationType
) => {
    const {filters, filterKeys} = data || {}
    let tableItems = []
    let totalItems
    let hasMore
    if (Array.isArray(data)) {
        tableItems = data
        totalItems = data.length
    } else if (data?.data) {
        tableItems = data.data
        totalItems = data.total
        hasMore = data.hasMore
    }

    const headerKeys = extractHeaderKeys(tableItems)
    const tableKeys = tables[name]?.visibleKeys || visibleKeys || headerKeys
    const keyTranslate = tables[name]?.keyTranslate || {}
    const externalKeys = tables[name]?.externalKey || {}
    const methodKeys = tables[name]?.methodKeys || {}
    const tableData = extractTableData(
        tableItems,
        visibleKeys,
        page,
        pageSize,
        search,
        sort,
        paginationType
    )
    const keyTypes = tables[name]?.keyTypes || {}

    return {
        tableKeys,
        tableData,
        keyTypes,
        tableItems,
        totalItems,
        hasMore,
        filters,
        filterKeys,
        keyTranslate,
        externalKeys,
        methodKeys,
    }
}

export const formatRateErrorMessage = (accounts) => {
    if (!accounts?.length) return ''
    let message = accounts
        .filter((account) => account?.statusRates?.errors?.length)
        .map((account) => {
            const {name, statusRates} = account
            return `${name} - ${statusRates.errors.join(', ')}`
        })
    return message.join(', ')
}

const StatusMeterReadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    color: white;
    background-color: ${({color}) => color};
    border-radius: 5px;
    font-weight: bold;
    padding: 5px;
`
